import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, CheckCircle2, Loader2, AlertCircle, Layers, Send, Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  segmentRangesFor,
  formatClock,
  STORY_SEGMENT_SECONDS,
} from '@/utils/storySegments';
import { PreviewMuteToggle } from '@/components/Upload/PreviewMuteToggle';
import { tr } from '@/contexts/LanguageContext';

// ============================================================================
// SegmentsPreviewStep — multi-segment publish screen
// ============================================================================
//
// What the user sees after picking "Saltar y dividir en partes":
//
//   ┌─────────────────────────────────────────┐
//   │  ← Volver        Dividir en 3 historias │
//   ├─────────────────────────────────────────┤
//   │                                         │
//   │     [vertical 9:16 preview of segment]  │
//   │                                         │
//   ├─────────────────────────────────────────┤
//   │  [1/3] [2/3] [3/3]   ← horizontal rail  │
//   ├─────────────────────────────────────────┤
//   │  Story 1/3 · 0:00 – 1:00   [×]          │
//   │  Story 2/3 · 1:00 – 2:00   [×]          │
//   │  Story 3/3 · 2:00 – 2:30   [×]          │
//   ├─────────────────────────────────────────┤
//   │       [Publicar 3 historias]            │
//   └─────────────────────────────────────────┘
//
// During publish we animate each row through 4 visual states:
//   queued → uploading (progress bar) → success ✓ / error ⚠
// All progress is SIMULATED in the frontend prototype. The real
// upload that happens in the background uploads ONE clip (the
// original file) so the user still gets a working story in the
// feed after the demo, but the UI shows the full 1/N · 2/N · 3/N
// flow as requested.
//
// PROPS
//   videoUrl              object URL of the picked file
//   durationSeconds       total length of the original video
//   onCancel              go back one step
//   onPublish             called after the simulated publish completes
//                         (parent triggers the real single-story upload)
//   onDiscardSegment(i)   user dropped segment i from the batch

export const SegmentsPreviewStep = ({
  videoUrl,
  durationSeconds,
  onCancel,
  onPublish,
  busy,
  publishPhase,           // null | 'uploading' | 'done' | 'error'
  uploadProgressPct,      // 0..100 (real upload progress for the single file)
}) => {
  const ranges = segmentRangesFor(durationSeconds);
  const [active, setActive] = useState(0);
  const [discarded, setDiscarded] = useState(() => new Set());
  const videoRef = useRef(null);

  // Looping window on the preview video so the active segment plays in
  // a tight loop while the user inspects the rail.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const [a, b] = ranges[active] || [0, 0];
    try { v.currentTime = a; } catch { /* iOS late-mount */ }
    v.play().catch(() => {});
    const onTime = () => {
      if (v.currentTime >= b || v.currentTime < a) {
        try { v.currentTime = a; } catch { /* noop */ }
      }
    };
    v.addEventListener('timeupdate', onTime);
    return () => v.removeEventListener('timeupdate', onTime);
  }, [active, videoUrl, durationSeconds]);

  const totalKept = ranges.length - discarded.size;

  const handleDiscard = (idx) => {
    if (busy) return;
    setDiscarded((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
    // If the active one was discarded, jump to the first kept one.
    if (active === idx) {
      const firstKept = ranges.findIndex((_, i) => i !== idx && !discarded.has(i));
      if (firstKept !== -1) setActive(firstKept);
    }
  };

  // ─── Simulated per-segment publish state ────────────────────────────
  // While `busy` is true we walk through the rows. The REAL upload
  // owns `uploadProgressPct`; we map that 0..100 onto the segments
  // proportionally so the user sees each row animate.
  const segmentStateFor = (idx) => {
    if (discarded.has(idx)) return 'discarded';
    if (!busy) return 'queued';
    if (publishPhase === 'done') return 'success';
    if (publishPhase === 'error') return 'error';
    // uploading: figure out which row to highlight
    const keptIndices = ranges
      .map((_, i) => i)
      .filter((i) => !discarded.has(i));
    const slot = keptIndices.indexOf(idx);
    if (slot === -1) return 'queued';
    const sliceWidth = 100 / Math.max(1, keptIndices.length);
    const rowStart = slot * sliceWidth;
    const rowEnd = rowStart + sliceWidth;
    if (uploadProgressPct >= rowEnd) return 'success';
    if (uploadProgressPct >= rowStart) return 'uploading';
    return 'queued';
  };
  const segmentLocalPct = (idx) => {
    if (!busy) return 0;
    const keptIndices = ranges
      .map((_, i) => i)
      .filter((i) => !discarded.has(i));
    const slot = keptIndices.indexOf(idx);
    if (slot === -1) return 0;
    const sliceWidth = 100 / Math.max(1, keptIndices.length);
    const rowStart = slot * sliceWidth;
    const local = ((uploadProgressPct - rowStart) / sliceWidth) * 100;
    return Math.max(0, Math.min(100, local));
  };

  return (
    <div
      className="absolute inset-0 z-30 flex flex-col bg-card"
      data-testid="story-segments-step"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-border flex items-center gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          data-testid="story-segments-back-btn"
          className="w-8 h-8 rounded-full hover:bg-secondary inline-flex items-center justify-center disabled:opacity-40"
          aria-label={tr("Volver")}
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-display font-bold leading-tight inline-flex items-center gap-1.5">
            <Layers className="w-4 h-4 text-primary" />{tr("Dividir en {0} historias", ranges.length)}</p>
          <p className="text-[11px] text-muted-foreground leading-tight">{tr("Cada parte dura máximo {0} s.", STORY_SEGMENT_SECONDS)}</p>
        </div>
      </div>

      {/* Preview of the active segment (9:16) */}
      <div className="bg-black aspect-[9/16] relative flex items-center justify-center">
        <video
          ref={videoRef}
          src={videoUrl}
          autoPlay
          loop={false}
          muted
          playsInline
          className="w-full h-full object-contain"
          data-testid="story-segments-active-preview"
        />
        {/* Active segment badge */}
        <div
          className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/65 backdrop-blur-md text-white text-[11px] font-bold"
          data-testid="story-segments-active-badge"
        >
          {active + 1}/{ranges.length}
        </div>
        {/* Range label */}
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-full bg-black/65 backdrop-blur-md text-white text-[11px] font-mono">
          {formatClock(ranges[active][0])} – {formatClock(ranges[active][1])}
        </div>
        {/* Iter 110 — mute toggle, pinned top-right so it doesn't
            overlap with the active-segment badge (top-left) or the
            range label (bottom-centre). */}
        <PreviewMuteToggle
          videoRef={videoRef}
          testIdSuffix="story-segments"
          className="absolute top-2 right-2 z-30"
        />
      </div>

      {/* Horizontal segment rail */}
      <div
        className="flex gap-2 overflow-x-auto px-3 py-3 border-b border-border"
        data-testid="story-segments-rail"
      >
        {ranges.map(([a, b], idx) => {
          const isDiscarded = discarded.has(idx);
          const isActive = idx === active && !isDiscarded;
          return (
            <button
              key={idx}
              type="button"
              onClick={() => !isDiscarded && !busy && setActive(idx)}
              disabled={isDiscarded || busy}
              data-testid={`story-segments-tile-${idx}`}
              className={`relative flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all ${
                isActive ? 'border-primary scale-105' : 'border-transparent opacity-60'
              } ${isDiscarded ? 'opacity-30 grayscale' : ''}`}
              style={{ width: 56, height: 84 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary/30 to-primary/10" />
              <span className="absolute top-0.5 left-0.5 px-1 py-0.5 rounded text-[8px] font-bold bg-black/65 text-white">
                {idx + 1}
              </span>
              <span className="absolute bottom-0.5 right-0.5 px-1 py-0.5 rounded text-[8px] font-mono bg-black/65 text-white">
                {Math.round(b - a)}s
              </span>
            </button>
          );
        })}
      </div>

      {/* Per-segment rows with progress */}
      <div
        className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5"
        data-testid="story-segments-list"
      >
        {ranges.map(([a, b], idx) => {
          const state = segmentStateFor(idx);
          const pct = segmentLocalPct(idx);
          return (
            <SegmentRow
              key={idx}
              idx={idx}
              total={ranges.length}
              from={a}
              to={b}
              state={state}
              pct={pct}
              onDiscard={() => handleDiscard(idx)}
              disabled={busy && state !== 'discarded'}
            />
          );
        })}
      </div>

      {/* Footer action — sticky publish button always visible above the
          keyboard / overlays. Safe-area padding handles iPhone notch. */}
      <div
        className="sticky bottom-0 left-0 right-0 z-10 p-3 border-t border-border bg-card pb-[max(0.75rem,env(safe-area-inset-bottom))]"
        data-testid="story-segments-publish-footer"
      >
        <Button
          onClick={() => onPublish?.({ keptIndices: ranges.map((_, i) => i).filter((i) => !discarded.has(i)) })}
          disabled={busy || totalKept === 0}
          data-testid="story-segments-publish-btn"
          className="w-full h-11 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5 font-semibold"
        >
          {busy ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {publishPhase === 'done' ? tr("¡Listo!") : tr("Publicando {0} historia{1}…", totalKept, totalKept === 1 ? '' : 's')}
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />{tr("Publicar {0} historia{1}", totalKept, totalKept === 1 ? '' : 's')}</>
          )}
        </Button>
      </div>
    </div>
  );
};

const SegmentRow = ({ idx, total, from, to, state, pct, onDiscard, disabled }) => {
  const label = `Story ${idx + 1}/${total}`;
  return (
    <div
      className={`relative rounded-lg border border-border bg-secondary/40 px-3 py-2 transition-opacity ${
        state === 'discarded' ? 'opacity-40' : ''
      }`}
      data-testid={`story-segments-row-${idx}`}
      data-state={state}
    >
      <div className="flex items-center gap-2">
        <span className="w-7 h-7 rounded-md bg-primary/15 text-primary inline-flex items-center justify-center text-[11px] font-bold">
          {idx + 1}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold leading-tight">{label}</p>
          <p className="text-[10px] text-muted-foreground font-mono leading-tight">
            {formatClock(from)} – {formatClock(to)} · {Math.round(to - from)}s
          </p>
        </div>
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold">
          <AnimatePresence mode="wait">
            {state === 'queued' && (
              <motion.span key="q" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-muted-foreground">{tr("En cola")}</motion.span>
            )}
            {state === 'uploading' && (
              <motion.span key="u" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-primary inline-flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" />{tr("Subiendo")}</motion.span>
            )}
            {state === 'success' && (
              <motion.span key="s" initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} className="text-emerald-400 inline-flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" />{tr("Listo")}</motion.span>
            )}
            {state === 'error' && (
              <motion.span key="e" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-destructive inline-flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" />{tr("Error")}</motion.span>
            )}
            {state === 'discarded' && (
              <motion.span key="d" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-muted-foreground line-through">{tr("Descartado")}</motion.span>
            )}
          </AnimatePresence>
        </span>
        <button
          type="button"
          onClick={onDiscard}
          disabled={disabled}
          aria-label={state === 'discarded' ? tr("Recuperar") : tr("Descartar")}
          data-testid={`story-segments-discard-${idx}`}
          className="w-7 h-7 rounded-full hover:bg-secondary inline-flex items-center justify-center disabled:opacity-30 text-muted-foreground hover:text-destructive"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {/* Progress bar (only meaningful while uploading) */}
      {(state === 'uploading' || state === 'success') && (
        <div className="mt-1.5 h-1 rounded-full bg-secondary overflow-hidden">
          <motion.div
            className="h-full bg-primary"
            initial={{ width: 0 }}
            animate={{ width: state === 'success' ? '100%' : `${pct}%` }}
            transition={{ ease: 'linear', duration: 0.2 }}
          />
        </div>
      )}
    </div>
  );
};

export default SegmentsPreviewStep;
